import { createContext, useContext } from 'react';
import type { Direction, Language } from './core';
import type { PluralForms } from './plural';
import type { Messages } from './messages/en';

export interface I18nValue {
  language: Language;
  dir: Direction;
  t: Messages;
  setLanguage: (language: Language) => void;
  fmt: (template: string, values: Record<string, string | number>) => string;
  /** A counted phrase in the correct plural form (Arabic: zero, one, two, few, many, other). */
  count: (forms: PluralForms, n: number, values?: Record<string, string | number>) => string;
  formatDate: (value: Date | string, options?: Intl.DateTimeFormatOptions, timeZone?: string) => string;
  formatTime: (value: Date | string, timeZone?: string) => string;
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
  formatCurrency: (value: number, currency?: string) => string;
  /** Joins items as a natural list in the current language ("A, B and C" / "أ وب وج"). */
  formatList: (items: readonly string[], type?: 'conjunction' | 'unit') => string;
  localized: (row: object, base: string) => string;
}

export const I18nContext = createContext<I18nValue | null>(null);

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>');
  return ctx;
}
